import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";

const values: { icon: string; title: string; desc: string }[] = [
  {
    icon: "ri-book-read-line",
    title: "专注剧情解读",
    desc: "围绕电影《出轨的妻子》，提供完整剧情介绍、人物关系与主题解读，帮助影迷看懂每一处细节。",
  },
  {
    icon: "ri-database-2-line",
    title: "清晰资源归类",
    desc: "按类型、地区与年份整理高清影视资源信息，配合筛选与排序，快速定位想看的内容。",
  },
  {
    icon: "ri-chat-smile-2-line",
    title: "真实影迷视角",
    desc: "收录精选影评与观后感，用观众的真实视角呈现影片的情绪与争议点。",
  },
];

const usage: { title: string; desc: string }[] = [
  { title: "浏览与筛选", desc: "在「电影分类」页按类型、地区、年份筛选，并使用排序功能快速找到目标影片。" },
  { title: "搜索资源", desc: "点击导航搜索入口，输入电影名称、类型、导演或主演关键词即可模糊搜索。" },
  { title: "查看详情", desc: "点击任一影视卡片进入详情页，查看剧情介绍、影片信息与相关推荐。" },
  { title: "参与影评", desc: "详情页影评区支持发布观后感，该功能仅为前端互动演示，不会保存数据。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 剧情介绍专业站 | 《出轨的妻子》hd",
    description:
      "《出轨的妻子》hd 是专注《出轨的妻子》影视资源信息与剧情介绍的专业网站，本文介绍站点定位、联系方式、使用说明与版权声明。",
    keywords: "关于我们,出轨的妻子,影视资源,剧情介绍,影评",
  });

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-6 rounded-lg bg-primary-950 px-6 py-10 text-background-50 md:px-12 md:py-14">
        <p className="font-label text-sm font-medium text-accent-400">关于我们</p>
        <h1 className="mt-2 font-heading text-2xl font-bold leading-snug md:text-4xl">
          专注《出轨的妻子》高清影视资源与剧情介绍
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
          《出轨的妻子》hd 是一个聚焦电影《出轨的妻子》及其同类型高清影视作品的资料型内容站。我们希望为影迷提供
          <strong className="mx-1 font-semibold text-accent-300">清晰、可读、易检索</strong>
          的影视信息，让每一部电影的类型、剧情与口碑都一目了然，帮助你在观看之前做出更从容的选择。
        </p>
      </header>

      <section className="mt-10" aria-label="我们的定位">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-5 w-1 rounded-full bg-accent-500"></span>
          我们在做什么
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div aria-label="使用说明">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            使用说明
          </h2>
          <ol className="mt-5 space-y-4">
            {usage.map((item, index) => (
              <li key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 font-label text-sm text-background-50">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 rounded-lg bg-accent-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-copyright-line text-lg text-accent-600" aria-hidden="true"></i>
              版权声明
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站为影视内容展示与设计演示站点，站内所有影视资源信息、剧情介绍、封面图片、影评及评论均为
              <strong className="mx-1 font-semibold text-foreground-800">虚构演示示例</strong>
              ，不对应任何真实影视作品或资源。本站不提供在线播放、下载与收藏功能，亦不存储任何用户数据。
              若涉及内容展示问题，欢迎通过下方邮箱与我们联系。
            </p>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-950">联系方式</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如对站点内容有建议、反馈或合作意向，欢迎发送邮件联系我们。
            </p>
            <a
              href="mailto:hello@chuguideqizi-hd.demo"
              className="mt-4 flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <i className="ri-mail-send-line text-xl text-primary-600" aria-hidden="true"></i>
              <span className="text-sm text-foreground-800">hello@chuguideqizi-hd.demo</span>
            </a>
            <p className="mt-3 font-label text-xs text-foreground-400">该邮箱为演示用途，仅作展示。</p>
          </div>

          <div className="rounded-lg bg-secondary-50 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-950">快速开始</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              想直接找到想看的电影？前往分类页按条件筛选，或使用搜索功能按关键词定位。
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-film-line" aria-hidden="true"></i>
                浏览电影分类
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
                搜索影视资源
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}